import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { getDistance } from "../functions/getDistance";
import lang from "../functions/lang";
import { Store } from "../interfaces/IStore";
import { AppContext } from "./AppContext";
import { FilterTemplate } from "./FilterTemplate";
import { Header } from "./Header";
import AirbnbResults from "./Icons/AirbnbResults";
import ChevronIcon from "./Icons/ChevronIcon";
import HotelResults from "./Icons/HotelResults";
import StoreResults from "./Icons/StoreResults";
import { MapContext } from "./MapContext";
import { ResultsList } from "./ResultsList";
import StoreCard from "./StoreCard";
declare global {
  interface Window {
    singleStore?: boolean;
    targetStore?: string;
    competitor?: {
      locationName?: string;
      coords?: {
        lat?: number | string;
        lng: number | string;
      };
      avgRatings: number;
      placeId: string | number;
      business_type: string;
    }[];
    data?: {
      [key: string]: any;
    };
  }
}
interface IOverlay_Desktop {
  showStore: boolean;
  setShowStore: (showStore: boolean) => void;
  showFilter: boolean;
  setShowFilter: (showFilter: boolean) => void;
}

export const Overlay_Desktop = ({
  showStore,
  setShowStore,
  showFilter,
  setShowFilter,
}: PropsWithChildren<IOverlay_Desktop>) => {
  const {
    productFiltersGlobal,
    businessFiltersGlobal,
    setBusinessFiltersGlobal,
    setProductFiltersGlobal,
  } = useContext(AppContext);

  const [showProductTypes, setShowProductTypes] = useState<boolean>(false);
  const [showBusinessTypes, setShowBusinessTypes] = useState<boolean>(false);
  const { currentStore, setCurrentStore, coords } = useContext(MapContext);
  const { stores } = useContext(AppContext);
  const [productTop, setProductTop] = useState<number>(0);
  const [businessTop, setBusinessTop] = useState<number>(0);
  const [searchError, setSearchError] = useState<boolean>(false);

  const handleBusinessFormChange = (e: InputEvent) => {
    const target = e.target as HTMLInputElement;
    if (businessFiltersGlobal !== target?.value)
      setBusinessFiltersGlobal(target.value);
    else setBusinessFiltersGlobal(undefined);
  };

  const handleProductFormChange = (e: InputEvent) => {
    const target = e.target as HTMLFormElement;
    let isInArray = productFiltersGlobal.some(
      (filter) => filter == target.value
    );

    if (!isInArray && target?.checked) {
      setProductFiltersGlobal([...productFiltersGlobal, target.value]);
    } else {
      setProductFiltersGlobal(
        productFiltersGlobal.filter((filter) => filter !== target.value)
      );
    }
  };

  const [nearest, setNearest] = useState<Store | undefined>();
  useEffect(() => {
    setNearest(stores[0]);
  }, [stores]);

  const productRef = useRef<HTMLDivElement>(null);
  const businessRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showStore) {
      setShowBusinessTypes(false);
      setShowProductTypes(false);
    }
  }, [showStore]);

  useEffect(() => {
    productRef.current &&
      setProductTop(productRef.current?.getBoundingClientRect().top);
    businessRef.current &&
      setBusinessTop(businessRef.current?.getBoundingClientRect().top - 3);
  }, []);

  return (
    <>
      <StoreCard
        currentStore={currentStore}
        showStore={showStore}
        closeStore={() => {
          setShowStore(false);
          setCurrentStore(undefined);
        }}
      />
      <FilterTemplate
        top={productTop}
        label={lang(
          "filters.filterHeaders.product",
          window.data?.language || "en"
        )}
        isOpen={showProductTypes}
        close={() => setShowProductTypes(false)}
        handleFormChange={handleProductFormChange}
        activeFilters={productFiltersGlobal}
        isMobile={false}
        filters={window.data?.products.map((p: string) => ({
          value: p,
          label: lang(`filters.products.${p}`, window.data?.language || "en"),
        }))}
        type={"product"}
      />

      <FilterTemplate
        top={businessTop}
        label={lang(
          "filters.filterHeaders.businessType",
          window.data?.language || "en"
        )}
        isOpen={showBusinessTypes}
        close={() => setShowBusinessTypes(false)}
        handleFormChange={handleBusinessFormChange}
        filters={window.data?.businessTypes.map((p: string) => ({
          value: p,
          label: lang(
            `filters.businessTypes.${p}`,
            window.data?.language || "en"
          ),
        }))}
        isMobile={false}
        activeFilters={
          businessFiltersGlobal !== undefined ? [businessFiltersGlobal] : []
        }
        type={"business"}
      />

      
        <StoreLocatorParentContainer>
        <StoreLocatorContainer> 
          <Header setSearchError={setSearchError} />
            <FilterHeader
              ref={productRef}
              onClick={(e) => {
                setShowProductTypes((showProductTypes) => !showProductTypes);
                setShowBusinessTypes(false);
                setShowStore(false);
              }}
              onLoad={(e) =>
                setProductTop(e.currentTarget.getBoundingClientRect().top)
              }>
              <FilterTitle>
                {lang(
                  "filters.filterHeaders.product",
                  window.data?.language || "en"
                )}
              </FilterTitle>

              <NotifWrapper>
                {productFiltersGlobal.length > 0 ? (
                  <Notif>{productFiltersGlobal.length}</Notif>
                ) : null}
                <ChevronWrapper isOpen={showProductTypes}>
                  <ChevronIcon />
                </ChevronWrapper>
              </NotifWrapper>
            </FilterHeader>
            <FilterHeader
              ref={businessRef}
              onClick={(e) => {
                setShowBusinessTypes((showBusinessTypes) => !showBusinessTypes);
                setShowProductTypes(false);
                setShowStore(false);
              }}
              onLoad={(e) =>
                setBusinessTop(e.currentTarget.getBoundingClientRect().top - 3)
              }>
              <FilterTitle>
                {lang(
                  "filters.filterHeaders.businessType",
                  window.data?.language || "en"
                )}
              </FilterTitle>
              <NotifWrapper>
                {businessFiltersGlobal ? (
                  <NotifBus>
                    {businessFiltersGlobal === "airbnb" ? (
                      <AirbnbResults />
                    ) : businessFiltersGlobal === "hotel" ? (
                      <HotelResults />
                    ) : (
                      <StoreResults />
                    )}
                  </NotifBus>
                ) : null}
                <ChevronWrapper isOpen={showBusinessTypes}>
                  <ChevronIcon />
                </ChevronWrapper>
              </NotifWrapper>
            </FilterHeader>
            <ResultsList
              label={lang(
                "filters.filterHeaders.results",
                window.data?.language || "en"
              )}
              onClick={(store: Store) => {
                setCurrentStore(store);
                setShowProductTypes(false);
                setShowBusinessTypes(false);
                setShowStore(true);
              }}
            />
          </StoreLocatorContainer>
        </StoreLocatorParentContainer>
     
      <Card isOpen={searchError}>
        <CardHeaderWrapper>
          <CardHeader>
            {lang("modal.no_nearby", window.data?.language || "en")}
          </CardHeader>
        </CardHeaderWrapper>
        <CardContentWrapper>
          {lang("modal.no_nearby_desc", window.data?.language || "en")}
        </CardContentWrapper>
        <CardContentWrapper>
          {lang("modal.nearest_store", window.data?.language || "en")}
          <br />
          {window.data?.language === "fr"
            ? nearest?.name_fr || nearest?.name_en
            : nearest?.name_en}{" "}
          (
          {nearest &&
            (
              getDistance(
                [coords.lat, coords.lng],
                [nearest?.latitude, nearest?.longitude]
              ) / 1000
            ).toFixed(0)}
          km)
        </CardContentWrapper>
        <CardButtonWrapper>
          <CardButton
            onClick={() => {
              setCurrentStore(nearest);
              setShowStore(true);
              setSearchError(false);
            }}>
            {lang("modal.set_nearest", window.data?.language || "en")}
          </CardButton>
          <CardButton onClick={() => setSearchError(false)}>
            {lang("modal.ok", window.data?.language || "en")}
          </CardButton>
        </CardButtonWrapper>
      </Card>
    </>
  );
};

const ChevronWrapper = styled.div<{ isOpen: boolean }>`
  transition: 0.3s;
  transform: ${(props) => (props.isOpen ? `rotate(180deg)` : "rotate(0deg)")};
`;
const StoreLocatorParentContainer = styled.div`
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  overflow: hidden;
  display: flex;
  flex-flow: column;
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
  pointer-events: none;
`;

const StoreLocatorContainer = styled.div`
  pointer-events: all;
  bottom: 0;
  margin-top: 2rem;
  width: 21rem;
  position: relative;
  border-top-left-radius: 1.6rem;
  border-top-right-radius: 1.6rem;
  z-index: 2;
  background-color: #fff;
  left: 2rem;
  box-shadow: 0 1px 7px 0 rgb(0 0 0 / 20%);
  overflow: scroll;
  display: flex;
  flex-flow: column;
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const FilterHeader = styled.div`
  height: 3.6rem;
  background-color: white;
  color: #522398;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1.5rem;
  cursor: pointer;
  border-bottom: 1px solid #c0bebe;
`;
const FilterTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.8px;
  margin-top: 0.4rem;
`;

const Chevron = styled.img`
  width: 11px;
  height: 17px;
`;

const Card = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.isOpen ? "all" : "none")};
  overflow: hidden;
  flex-flow: column;
  transition: 0.3s;
  flex-flow: column;
  flex-grow: 1;
  flex-shrink: 1;
  border-radius: 1.6rem;
  min-width: 20rem;
  max-width: 23rem;
  box-shadow: 0 1px 7px 0 rgb(0 0 0 / 20%);
  overflow: hidden;
  background: white;
  overflow: auto;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const CardHeaderWrapper = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #c0bebe;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const CardHeader = styled.h4`
  margin: 0;
  font-weight: 800;
  font-size: 18px;
  letter-spacing: -0.5px;
  color: #522398;
  text-align: center;
`;

const CardContentWrapper = styled.div`
  padding: 1rem 1.5rem;
  flex-grow: 1;
  flex-shrink: 1;
  display: flex;
  flex-direction: column;
  ::-webkit-scrollbar {
    border-radius: 30px;
  }
  text-align: center;
`;

const CardButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  padding: 1rem;
`;

const CardButton = styled.button`
  border-radius: 36px;
  background-color: #522398;
  color: #fff;
  font-size: 11px;
  height: auto
  outline: none;
  border: none;
  font-family: inherit;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 1.38;
  padding: 0.6rem 1.6rem;
  text-align:center;
  justify-content:center;
  display:flex;
`;

const NotifWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 0;
  flex-shrink: 0;
`;

const Notif = styled.span`
  background-color: #522398;
  border-radius: 50%;
  color: white;
  height: 1.3rem;
  width: 1.3rem;
  text-align: center;
  justify-content: center;
  align-items: center;
  margin-right: 1rem;
`;

const NotifBus = styled.span`
  height: auto;
  width: auto;
  text-align: center;
  justify-content: center;
  align-items: center;
  margin-right: 1rem;
`;
